import React, { useMemo, useState } from 'react';
import { api } from '../api';
import { statusInfo, fmtDate, compact, manualProgress } from '../lib/ui';
import { EmptyState } from '../lib/illustrations';
import ManualBar from '../components/ManualBar';
import { IconPlus, IconSend, IconEye, IconCalendar, IconRefresh, IconCopy, IconTrash, IconList, IconCheckCircle, IconExternal } from '../lib/icons';

const FILTERS = [
  { id: '', label: 'Todas' },
  { id: 'published', label: 'Publicadas' },
  { id: 'scheduled', label: 'Agendadas' },
  { id: 'draft', label: 'Rascunhos' },
  { id: 'manual', label: 'Manuais' },
  { id: 'partial', label: 'Parciais' },
  { id: 'failed', label: 'Falhas' },
];

export default function Publications({ publications, accounts, platforms, onRefresh, navigate }) {
  const [filter, setFilter] = useState('');
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const items = useMemo(
    () => (filter ? publications.filter((p) => p.status === filter) : publications),
    [publications, filter]
  );

  const run = async (key, fn) => {
    setBusy(key);
    setError('');
    try {
      await fn();
      onRefresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy('');
    }
  };

  const publish = (id) => run(`pub-${id}`, () => api.post(`/publications/${id}/publish`, {}));
  const schedule = (id) => {
    const value = window.prompt('Data e hora do agendamento (AAAA-MM-DDTHH:MM):', '');
    if (!value) return;
    const when = new Date(value).getTime();
    if (Number.isNaN(when)) {
      setError('Data inválida. Use o formato AAAA-MM-DDTHH:MM (ex.: 2026-10-01T14:30).');
      return;
    }
    if (when <= Date.now()) {
      setError('A data agendada precisa estar no futuro.');
      return;
    }
    return run(`sch-${id}`, () => api.post(`/publications/${id}/schedule`, { scheduleAt: value }));
  };
  const cancelSchedule = (id) => run(`can-${id}`, () => api.post(`/publications/${id}/cancel-schedule`, {}));
  const duplicate = (id) => run(`dup-${id}`, () => api.post(`/publications/${id}/duplicate`, {}));
  const remove = (id) => run(`del-${id}`, () => api.del(`/publications/${id}`));
  const metrics = (id) => run(`met-${id}`, () => api.post(`/publications/${id}/refresh-metrics`, {}));

  const openSite = (url) => {
    if (url) window.open(url, '_blank', 'noopener');
  };

  const confirmPlatform = async (pub, pid, accountId) => {
    const r = (pub.results || {})[pid] || {};
    const entry = accountId ? (r.accounts || {})[accountId] : r;
    const prefill = entry && entry.ok ? entry.url || '' : '';
    const url = window.prompt('Link do post publicado (opcional):', prefill);
    if (url === null) return;
    const key = `conf-${pub.id}-${pid}:${accountId || ''}`;
    setBusy(key);
    setError('');
    try {
      const updated = await api.post(`/publications/${pub.id}/confirm`, {
        platform: pid,
        accountId: accountId || undefined,
        url: url || '',
      });
      setDetail(updated);
      onRefresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy('');
    }
  };

  const accountMap = useMemo(() => {
    const map = {};
    (accounts || []).forEach((acc) => {
      map[acc.id] = acc;
    });
    return map;
  }, [accounts]);

  const detailRows = useMemo(() => {
    if (!detail) return [];
    const results = detail.results || {};
    const metrics = detail.metrics || {};
    const accountTargets = detail.accountTargets || [];
    const rows = [];
    (detail.targets || []).forEach((pid) => {
      const r = results[pid];
      const perAccount = r && r.accounts && Object.keys(r.accounts).length ? r.accounts : null;
      const owned = accountTargets.filter((t) => t && t.platform === pid);
      if (perAccount) {
        Object.keys(perAccount).forEach((aid) =>
          rows.push({
            key: `${pid}:${aid}`,
            platform: pid,
            accountId: aid,
            entry: perAccount[aid],
            metric: metrics[pid],
            acct: accountMap[aid] || null,
          })
        );
      } else if (!r && owned.length > 0) {
        owned.forEach((t) =>
          rows.push({
            key: `${pid}:${t.accountId}`,
            platform: pid,
            accountId: t.accountId,
            entry: null,
            metric: metrics[pid],
            acct: accountMap[t.accountId] || null,
          })
        );
      } else {
        rows.push({ key: pid, platform: pid, accountId: null, entry: r, metric: metrics[pid], acct: null });
      }
    });
    return rows;
  }, [detail, accountMap]);

  const colorOf = (pid) => {
    const p = (platforms || []).find((x) => x.id === pid);
    return p ? p.color : '#6d5cff';
  };

  return (
    <div>
      {error ? <div className="banner err">{error}</div> : null}

      <div className="filters">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            className={`filter ${filter === f.id ? 'on' : ''}`}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
          </button>
        ))}
        <button type="button" className="filter" onClick={() => navigate('composer')}>
          <span className="btn-ico">
            <IconPlus size={13} />
          </span>
          Nova
        </button>
      </div>

      {items.length === 0 ? (
        <EmptyState kind="search" size={180} title="Nenhuma publicação com esse filtro.">
          <button type="button" className="btn primary small" style={{ marginTop: 4 }} onClick={() => navigate('composer')}>
            <span className="btn-ico">
              <IconSend size={14} />
            </span>
            Criar publicação
          </button>
        </EmptyState>
      ) : (
        <div className="list">
          {items.map((pub) => {
            const info = statusInfo(pub.status);
            const results = pub.results || {};
            const metricsObj = pub.metrics || {};
            const metricValues = Object.values(metricsObj);
            const totalLikes = metricValues.reduce((acc, m) => acc + (m.likes || 0), 0);
            const prog = manualProgress(pub);

            return (
              <div className="row" key={pub.id}>
                <div style={{ minWidth: 0 }}>
                  <div className="title">
                    {pub.title || 'Sem título'}{' '}
                    <span className={`badge ${info.cls}`} style={{ marginLeft: 6 }}>
                      {info.label}
                    </span>
                    {prog.total > 0 && prog.pending > 0 ? (
                      <span className="badge warn" style={{ marginLeft: 6 }}>
                        {prog.done}/{prog.total} confirmadas
                      </span>
                    ) : null}
                  </div>
                  <div className="meta">
                    {(pub.targets || []).join(' · ')}
                    {pub.status === 'scheduled' ? ` · agendada para ${fmtDate(pub.scheduleAt)}` : ''}
                    {pub.publishedAt ? ` · publicada em ${fmtDate(pub.publishedAt)}` : ''}
                    {Object.keys(results).length > 0
                      ? ` · ${Object.values(results).filter((r) => r.ok).length}/${Object.keys(results).length} OK`
                      : ''}
                    {metricValues.length > 0 ? ` · ${compact(totalLikes)} curtidas` : ''}
                    {pub.mediaUrls && pub.mediaUrls.length > 1 && pub.rotateMedia
                      ? ` · rotação ${((pub.mediaIndex || 0) + 1)}/${pub.mediaUrls.length}`
                      : ''}
                  </div>
                </div>

                <div className="actions">
                  <button type="button" className="btn ghost small" onClick={() => setDetail(pub)}>
                    <span className="btn-ico">
                      <IconEye size={14} />
                    </span>
                    Detalhes
                  </button>
                  {pub.status !== 'scheduled' ? (
                    <button
                      type="button"
                      className="btn primary small"
                      disabled={busy === `pub-${pub.id}`}
                      onClick={() => publish(pub.id)}
                    >
                      <span className="btn-ico">
                        <IconSend size={14} />
                      </span>
                      {busy === `pub-${pub.id}` ? '…' : 'Publicar'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn ghost small"
                      disabled={busy === `can-${pub.id}`}
                      onClick={() => cancelSchedule(pub.id)}
                    >
                      Cancelar
                    </button>
                  )}
                  {pub.status !== 'scheduled' ? (
                    <button
                      type="button"
                      className="btn success small"
                      disabled={busy === `sch-${pub.id}`}
                      onClick={() => schedule(pub.id)}
                    >
                      <span className="btn-ico">
                        <IconCalendar size={14} />
                      </span>
                      Agendar
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="btn ghost small"
                    disabled={busy === `met-${pub.id}`}
                    onClick={() => metrics(pub.id)}
                  >
                    <span className="btn-ico">
                      <IconRefresh size={14} />
                    </span>
                    Métricas
                  </button>
                  <button type="button" className="btn ghost small" onClick={() => duplicate(pub.id)}>
                    <span className="btn-ico">
                      <IconCopy size={14} />
                    </span>
                    Duplicar
                  </button>
                  <button type="button" className="btn danger small" onClick={() => remove(pub.id)}>
                    <span className="btn-ico">
                      <IconTrash size={14} />
                    </span>
                    Excluir
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {detail ? (
        <div className="modal-bg" onClick={() => setDetail(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3>{detail.title || 'Sem título'}</h3>
              <button type="button" className="btn ghost small" onClick={() => setDetail(null)}>
                Fechar
              </button>
            </div>

            <div className="preview">{detail.content || '(sem conteúdo)'}</div>

            <div className="hint" style={{ marginTop: 14 }}>
              Plataformas: {(detail.targets || []).join(', ')}
              {(detail.accountTargets || []).length > 0
                ? ` · ${detail.accountTargets.length} conta(s) selecionada(s)`
                : ''}
              {detail.publishedAt ? ` · publicada em ${fmtDate(detail.publishedAt)}` : ''}
              {detail.scheduleAt ? ` · agendada para ${fmtDate(detail.scheduleAt)}` : ''}
            </div>

            <div className="section-head" style={{ marginTop: 18 }}>
              <h2>
                <span className="head-ico">
                  <IconList size={16} />
                </span>
                Resultados por conta
              </h2>
            </div>
            <ManualBar
              rows={detailRows}
              colorOf={colorOf}
              busy={busy}
              busyKey={(pid, aid) => `conf-${detail.id}-${pid}:${aid || ''}`}
              onConfirm={(pid, aid) => confirmPlatform(detail, pid, aid)}
            />
            <div className="list">
              {detailRows.map(({ key, platform, accountId, entry, metric }) => {
                const acct = accountId ? accountMap[accountId] : null;
                const name = acct ? acct.label || acct.username || accountId : platform;
                const pendingManual = entry && entry.manual && entry.pending && !entry.ok;
                const busyKey = `conf-${detail.id}-${platform}:${accountId || ''}`;
                return (
                  <div className="row" key={key}>
                    <div style={{ minWidth: 0 }}>
                      <div className="title">
                        {name}
                        {acct && acct.profile ? <span className="meta"> · {acct.profile}</span> : null}
                      </div>
                      {entry ? (
                        <>
                          <div className={entry.ok ? 'result-ok' : 'result-err'}>
                            {entry.ok
                              ? entry.message || 'Publicado'
                              : pendingManual
                                ? 'Aguardando publicação manual'
                                : entry.error}
                          </div>
                          {entry.url ? (
                            <div className="meta">
                              <a className="mono" href={entry.url} target="_blank" rel="noopener noreferrer">
                                {entry.url}
                              </a>
                            </div>
                          ) : null}
                          <div className="meta">{fmtDate(entry.at)}</div>
                        </>
                      ) : (
                        <div className="meta">Aguardando envio</div>
                      )}
                      {pendingManual && acct && acct.profile ? (
                        <div className="hint">Use o perfil “{acct.profile}” no navegador antes de abrir o site.</div>
                      ) : null}
                      {metric ? (
                        <div className="meta">
                          {metric.likes} curtidas · {metric.comments} comentários · {metric.shares} compartilhamentos ·{' '}
                          {metric.views} views
                          {metric.source ? ` (${metric.source})` : ''}
                        </div>
                      ) : null}
                    </div>
                    {pendingManual ? (
                      <div className="actions">
                        <button type="button" className="btn primary small" onClick={() => openSite(entry.url)}>
                          <span className="btn-ico">
                            <IconExternal size={14} />
                          </span>
                          Abrir site
                        </button>
                        <button
                          type="button"
                          className="btn success small"
                          disabled={busy === busyKey}
                          onClick={() => confirmPlatform(detail, platform, accountId)}
                        >
                          <span className="btn-ico">
                            <IconCheckCircle size={14} />
                          </span>
                          {busy === busyKey ? '…' : 'Confirmar'}
                        </button>
                      </div>
                    ) : (
                      <span className={`badge ${entry && entry.ok ? 'ok' : entry ? 'err' : 'muted'}`}>
                        {entry ? (entry.ok ? 'OK' : 'Falhou') : '—'}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
